/**
 * TAB 1 — House Treasury Dashboard (requirement §9 to §11, AC-05, AC-06).
 *
 * Eleven KPI cards and five graphs over one filtered period.
 *
 * Three of the cards are point-in-time rather than period figures — treasury
 * balance, VC in distribution and VC in players. They are grouped separately
 * and labelled, because reading them as "movement during the selected range"
 * would be badly wrong.
 */

import * as React from "react";
import { Box, Grid, Typography, Chip } from "@mui/material";
import ReactECharts from "echarts-for-react";

import {
  KpiCard,
  TreasuryFilters,
  StatusChip,
  Panel,
  SectionTitle,
  ErrorBar,
  Loading,
  DataTable,
  fmtVC,
  fmtPct,
  GOLD,
  BORDER,
} from "../components/common";
import { getDashboard } from "../api";

// Shared echarts styling so every chart in the module reads as one system.
const chartBase = {
  backgroundColor: "transparent",
  textStyle: { color: "#BDBDBD", fontFamily: "Inter, sans-serif" },
  grid: { left: 60, right: 24, top: 40, bottom: 40 },
  tooltip: { trigger: "axis", backgroundColor: "#242426", borderColor: BORDER, textStyle: { color: "#FFF" } },
  legend: { textStyle: { color: "#BDBDBD" }, top: 4 },
};

const axis = (name) => ({
  type: "category",
  name,
  axisLine: { lineStyle: { color: "#555" } },
  axisLabel: { color: "#9E9E9E", fontSize: 10 },
});

const valueAxis = (name) => ({
  type: "value",
  name,
  nameTextStyle: { color: "#9E9E9E", fontSize: 10 },
  axisLine: { lineStyle: { color: "#555" } },
  splitLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
  axisLabel: { color: "#9E9E9E", fontSize: 10 },
});

export const DashboardTab = ({ filters, onFiltersChange }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getDashboard(filters);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const k = data?.kpis || {};
  const g = data?.graphs || {};

  const movementOption = React.useMemo(
    () => ({
      ...chartBase,
      legend: { ...chartBase.legend, data: ["VC Issued", "VC Returned"] },
      xAxis: { ...axis(), data: (g.vcMovement || []).map((r) => r.date) },
      yAxis: valueAxis("VC"),
      series: [
        {
          name: "VC Issued",
          type: "bar",
          data: (g.vcMovement || []).map((r) => r.issued),
          itemStyle: { color: "#42A5F5" },
        },
        {
          name: "VC Returned",
          type: "bar",
          data: (g.vcMovement || []).map((r) => r.returned),
          itemStyle: { color: "#66BB6A" },
        },
      ],
    }),
    [g.vcMovement]
  );

  const gameplayOption = React.useMemo(
    () => ({
      ...chartBase,
      legend: { ...chartBase.legend, data: ["Bets", "Wins", "GGR"] },
      xAxis: { ...axis(), data: (g.gameplay || []).map((r) => r.date) },
      yAxis: valueAxis("VC"),
      series: [
        {
          name: "Bets",
          type: "bar",
          data: (g.gameplay || []).map((r) => r.bets),
          itemStyle: { color: "#7E57C2" },
        },
        {
          name: "Wins",
          type: "bar",
          data: (g.gameplay || []).map((r) => r.wins),
          itemStyle: { color: "#EF5350" },
        },
        {
          name: "GGR",
          type: "line",
          smooth: true,
          data: (g.gameplay || []).map((r) => r.ggr),
          itemStyle: { color: GOLD },
          lineStyle: { width: 2 },
        },
      ],
    }),
    [g.gameplay]
  );

  const ggrTrendOption = React.useMemo(
    () => ({
      ...chartBase,
      legend: { show: false },
      xAxis: { ...axis(), data: (g.ggrTrend || []).map((r) => r.date) },
      yAxis: valueAxis("GGR"),
      series: [
        {
          name: "GGR",
          type: "line",
          smooth: true,
          areaStyle: { color: "rgba(255,193,7,0.15)" },
          data: (g.ggrTrend || []).map((r) => r.ggr),
          itemStyle: { color: GOLD },
          lineStyle: { width: 2 },
        },
      ],
    }),
    [g.ggrTrend]
  );

  const distributionOption = React.useMemo(
    () => ({
      ...chartBase,
      grid: { left: 130, right: 30, top: 20, bottom: 30 },
      tooltip: { ...chartBase.tooltip, trigger: "item" },
      legend: { show: false },
      xAxis: valueAxis("VC"),
      yAxis: {
        type: "category",
        data: (g.vcDistribution || []).map((r) => r.role).reverse(),
        axisLine: { lineStyle: { color: "#555" } },
        axisLabel: { color: "#9E9E9E", fontSize: 10 },
      },
      series: [
        {
          type: "bar",
          data: (g.vcDistribution || []).map((r) => r.balance).reverse(),
          itemStyle: { color: "#26A69A" },
          label: {
            show: true,
            position: "right",
            color: "#BDBDBD",
            fontSize: 10,
            formatter: (p) => fmtVC(p.value),
          },
        },
      ],
    }),
    [g.vcDistribution]
  );

  const bonusOption = React.useMemo(
    () => ({
      ...chartBase,
      legend: { ...chartBase.legend, data: ["Hourly Bonus", "Daily Bonus", "Other Bonus"] },
      xAxis: { ...axis(), data: (g.bonuses || []).map((r) => r.date) },
      yAxis: valueAxis("VC"),
      series: [
        {
          name: "Hourly Bonus",
          type: "bar",
          stack: "bonus",
          data: (g.bonuses || []).map((r) => r.hourly),
          itemStyle: { color: "#FF7043" },
        },
        {
          name: "Daily Bonus",
          type: "bar",
          stack: "bonus",
          data: (g.bonuses || []).map((r) => r.daily),
          itemStyle: { color: "#FFA726" },
        },
        {
          name: "Other Bonus",
          type: "bar",
          stack: "bonus",
          data: (g.bonuses || []).map((r) => r.other),
          itemStyle: { color: "#FFCA28" },
        },
      ],
    }),
    [g.bonuses]
  );

  const ownerColumns = [
    { key: "gameOwnerName", label: "Game Owner" },
    { key: "bets", label: "Bets", align: "right", render: (r) => fmtVC(r.bets) },
    { key: "wins", label: "Wins", align: "right", render: (r) => fmtVC(r.wins) },
    {
      key: "ggr",
      label: "GGR",
      align: "right",
      render: (r) => (
        <span style={{ color: r.ggr >= 0 ? "#A5D6A7" : "#EF9A9A" }}>{fmtVC(r.ggr)}</span>
      ),
    },
    { key: "rtp", label: "RTP", align: "right", render: (r) => fmtPct(r.rtp) },
    { key: "bonuses", label: "Bonuses", align: "right", render: (r) => fmtVC(r.bonuses) },
  ];

  return (
    <Box>
      <TreasuryFilters filters={filters} onChange={onFiltersChange} />
      <ErrorBar error={error} onClose={() => setError("")} />

      {loading && !data ? (
        <Loading height={320} />
      ) : (
        <>
          {/* Point-in-time position — deliberately separated from the period
              figures below, since these are current balances, not movement. */}
          <SectionTitle
            action={
              <Chip
                size="small"
                label="Current position — not affected by the date filter"
                sx={{ backgroundColor: "#242426", color: "#9E9E9E", fontSize: "0.65rem" }}
              />
            }
          >
            Treasury Position
          </SectionTitle>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="CURRENT HOUSE TREASURY BALANCE"
                value={fmtVC(k.currentTreasuryBalance)}
                suffix="VC"
                accent={GOLD}
                hint="Authorized supply minus everything issued, plus everything returned. This is the live balance, not the movement over the selected period."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="TOTAL VC IN DISTRIBUTION"
                value={fmtVC(k.vcInDistribution)}
                suffix="VC"
                hint="Combined balance held by Game Owners, Master Distributors, Distributors, Sub-Distributors and Agents. Players are counted separately."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="TOTAL VC IN PLAYERS"
                value={fmtVC(k.vcInPlayers)}
                suffix="VC"
                hint="Current VC held by all player wallets."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Panel
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "#9E9E9E", fontSize: "0.7rem", mb: 1 }}>
                  TREASURY STATUS
                </Typography>
                <Box>
                  <StatusChip value={k.treasuryStatus} />
                </Box>

              </Panel>
            </Grid>
          </Grid>

          <SectionTitle
            action={
              <Chip
                size="small"
                label={data?.range?.label || ""}
                sx={{ backgroundColor: "#242426", color: GOLD, fontSize: "0.65rem" }}
              />
            }
          >
            Period Activity
          </SectionTitle>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="VC GENERATED" value={fmtVC(k.totalGenerated)} suffix="VC" accent={GOLD}
                hint="Fresh VC created by Super Admin recharges. It is minted into the House Treasury and issued straight out, so the standing balance is never spent." />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="VC ISSUED" value={fmtVC(k.totalIssued)} suffix="VC"
                hint="VC transferred out of the House Treasury into Game Owner accounts. Matches VC Generated for the same period. Hierarchy transfers are not issuance." />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="VC RETURNED" value={fmtVC(k.totalReturned)} suffix="VC"
                hint="VC returned from Game Owners to the House Treasury. Ordinary hierarchy returns are not counted here." />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="TOTAL BETS" value={fmtVC(k.totalBets)} suffix="VC" />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="TOTAL WINS" value={fmtVC(k.totalWins)} suffix="VC" />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="TOTAL LOSS" value={fmtVC(k.totalLoss)} suffix="VC"
                hint="Player loss over the period, calculated as Bets minus Wins." />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="GGR" value={fmtVC(k.ggr)} suffix="VC" accent={GOLD}
                hint="Gross Gaming Revenue = Total Bets minus Total Wins." />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="RTP" value={fmtPct(k.rtp)}
                hint="Return to Player = Total Wins divided by Total Bets." />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="TOTAL BONUSES" value={fmtVC(k.totalBonuses)} suffix="VC"
                hint="Hourly and daily bonus wheel VC. Funded by Game Owner wallets." />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="ROLLBACKS" value={fmtVC(k.totalRollbacks)} suffix="VC"
                hint="VC returned by cancelled rounds. Shown so bets and wins reconcile." />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="LIFETIME ISSUED" value={fmtVC(k.lifetimeIssued)} suffix="VC" />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="LIFETIME RETURNED" value={fmtVC(k.lifetimeReturned)} suffix="VC" />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="LIFETIME GENERATED" value={fmtVC(k.lifetimeGenerated)} suffix="VC"
                hint="Total fresh VC ever created by Super Admin recharges." />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Panel>
                <SectionTitle>VC Movement</SectionTitle>
                <ReactECharts option={movementOption} style={{ height: 280 }} />
              </Panel>
            </Grid>
            <Grid item xs={12} md={6}>
              <Panel>
                <SectionTitle>Bets vs Wins vs GGR</SectionTitle>
                <ReactECharts option={gameplayOption} style={{ height: 280 }} />
              </Panel>
            </Grid>
            <Grid item xs={12} md={6}>
              <Panel>
                <SectionTitle>GGR Trend</SectionTitle>
                <ReactECharts option={ggrTrendOption} style={{ height: 260 }} />
              </Panel>
            </Grid>
            <Grid item xs={12} md={6}>
              <Panel>
                <SectionTitle>VC Distribution</SectionTitle>
                <ReactECharts option={distributionOption} style={{ height: 260 }} />
              </Panel>
            </Grid>
            <Grid item xs={12} md={6}>
              <Panel>
                <SectionTitle>Bonus VC</SectionTitle>
                <ReactECharts option={bonusOption} style={{ height: 260 }} />
              </Panel>
            </Grid>
            <Grid item xs={12} md={6}>
              <Panel>
                <SectionTitle>Game Owner Performance</SectionTitle>
                <DataTable
                  dense
                  flat
                  columns={ownerColumns}
                  rows={g.gameOwnerPerformance || []}
                  emptyMessage="No gameplay recorded for the selected period."
                />
              </Panel>
            </Grid>
          </Grid>

          {data?.meta?.walletsSyncedAt && (
            <Typography sx={{ color: "#666", fontSize: "0.65rem", mt: 2, textAlign: "right" }}>
              Wallet positions synced {new Date(data.meta.walletsSyncedAt).toLocaleString()}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default DashboardTab;
